import type { Seat } from '../types/state'

/**
 * Human PvP clock. A pool per player. It starts at 2:00 and ticks only
 * while that seat can act. A later turn adds time, up to the cap. A turn
 * here is a summon, a set, and an attack — not a combo.
 *
 * The opening turn does not add time; the first change of turn does.
 * The server shows this pool on the chip and loses the duel at zero.
 */
export const DUEL_CLOCK_START_MS = 120_000
/** Bank ceiling. Above the start so a turn grant is visible. */
export const DUEL_CLOCK_MAX_MS = 180_000
/** Added when your own turn begins. Capped at DUEL_CLOCK_MAX_MS. */
export const DUEL_CLOCK_OWN_TURN_MS = 30_000
/** Added when the opponent's turn begins, for one trap answer. */
export const DUEL_CLOCK_OPP_TURN_MS = 8_000
/** Client paints this as urgent. */
export const DUEL_CLOCK_URGENT_MS = 8_000

export interface DuelClockPayload {
  remainingMs: [number, number]
  /** Whose clock is ticking right now. Null while nobody can act. */
  runningSeat: Seat | null
  /** Server `Date.now()` when this snapshot was taken. */
  at: number
}

/**
 * Applies the start-of-turn refresh. Both seats gain time; neither may
 * exceed the cap.
 */
export function grantDuelClockTurn(
  remainingMs: [number, number],
  turnPlayer: Seat,
): [number, number] {
  const other = (turnPlayer === 0 ? 1 : 0) as Seat
  const next: [number, number] = [remainingMs[0], remainingMs[1]]
  next[turnPlayer] = Math.min(
    DUEL_CLOCK_MAX_MS,
    next[turnPlayer] + DUEL_CLOCK_OWN_TURN_MS,
  )
  next[other] = Math.min(
    DUEL_CLOCK_MAX_MS,
    next[other] + DUEL_CLOCK_OPP_TURN_MS,
  )
  return next
}
